<template>
  <div class="flex h-full w-full flex-col justify-between overflow-y-auto">
    <div class="min-h-0 flex-grow overflow-y-auto">
      <div class="flex min-h-full flex-col justify-center py-4">
        <h1
          class="gc-font-title mb-10 mt-5 text-center font-text-pop-title text-text-pop-title">
          {{ props.recovery?.message_title }}
        </h1>
        <div
          v-if="props.recovery && props.recovery.message_text"
          class="font-primary mb-6 text-center text-small-titles md:mb-14">
          {{ props.recovery.message_text }}
        </div>
        <CustomInput
          id="signUpemail"
          customDivClass="mb-4 text-xs w-full"
          customLabelClass="text-small-titles font-primary"
          customInputClass="w-full mt-1 h-[46px]"
          :class="{ warning: emailWarning }"
          :label="$t('general.email')"
          placeholder="john.doe@mail.com"
          @update:text="fillEmail" />

        <div
          class="font-primary mb-2 mt-10 flex items-center"
          :class="{ warning: termsWarning }">
          <input
            id="accept-terms"
            name="accept-terms"
            type="checkbox"
            v-model="acceptTerms"
            @change="handleTermsChange"
            class="mr-2" />
          <label class="underline-on-hover text-xs" @click.stop="viewPolicies">
            {{ $t('GDPRShortConsentText') }}
          </label>
        </div>
      </div>
    </div>

    <div class="my-5 mt-10 flex justify-around gap-2">
      <CustomButton
        v-if="isMobile"
        class="backButton"
        width="w-full"
        :label="$t('general.back')"
        icon="chevron-left"
        customClass="secondary"
        @click="emit('close')" />
      <CustomButton
        class="signUpButton"
        width="w-full"
        :label="$t('general.submit')"
        @click="doSubmit" />
    </div>
  </div>

  <CustomDrawer
    v-model="policiesVisible"
    :position="isMobile ? 'bottom' : 'right'"
    :width="isMobile ? 'w-full' : 'w-[35%]'"
    height="h-auto">
    <template #drawerContent>
      <GDPRPolicyText @close="policiesVisible = false" />
    </template>
  </CustomDrawer>
</template>

<script setup>
import { ref, defineAsyncComponent } from 'vue';
import CustomInput from '@/components/customComponents/CustomInput.vue';
import CustomButton from '@/components/customComponents/CustomButton.vue';
import GDPRPolicyText from '@/components/PersonalData/GDPRPolicyText.vue';
import { useScreenSize } from '@composables/useScreenSize';
import { validateEmail } from '@composables/useEmailValidator';
import { useShoppingRecoveryStore } from '@stores/shoppingRecovery.store';

const CustomDrawer = defineAsyncComponent(
  () => import('@/components/customComponents/CustomDrawer.vue'),
);

const emit = defineEmits(['showSuccessMessage', 'close']);

const shoppingRecoveryStore = useShoppingRecoveryStore();
const { sendRecoveryPromotionForm } = shoppingRecoveryStore;

const props = defineProps({
  recovery: {
    type: Object,
    required: false,
  },
});

const { isMobile } = useScreenSize();

const policiesVisible = ref(false);
const acceptTerms = ref(false);

const email = ref('');

const emailWarning = ref(false);
const termsWarning = ref(false);

const showSuccessMessage = ref(false);

const validateFields = () => {
  let isValid = true;

  if (!email.value) {
    emailWarning.value = true;
    isValid = false;
  } else if (!validateEmail(email.value)) {
    emailWarning.value = true;
    isValid = false;
  } else {
    emailWarning.value = false;
  }

  if (!acceptTerms.value) {
    termsWarning.value = true;
    isValid = false;
  } else {
    termsWarning.value = false;
  }

  return isValid;
};

const doSubmit = async () => {
  const valid = validateFields();
  if (!valid) {
    return;
  }

  const data = {
    promoId: props.recovery.promoId,
    frequency: props.recovery.frequency,
    context: props.recovery.context || 'BOOKINGENGINE',
    trigger: props.recovery.trigger,
    email: email.value,
  };

  try {
    await sendRecoveryPromotionForm(data).then((response) => {
      if (response && response.data.status && !showSuccessMessage.value) {
        showSuccessMessage.value = true;
        emit('showSuccessMessage');
      }
    });
  } catch (error) {
    console.log('submit failed:', error);
  }
};

const viewPolicies = () => {
  policiesVisible.value = true;
};

const fillEmail = (value) => {
  email.value = value;
  emailWarning.value = false;
  checkFormValidity();
};

const handleTermsChange = () => {
  termsWarning.value = !acceptTerms.value;
  checkFormValidity();
};

const checkFormValidity = () => {
  const isEmpty = !email.value && !acceptTerms.value;
  const isValid =
    email.value && validateEmail(email.value) && acceptTerms.value;
  return isEmpty || isValid;
};
</script>
<style scoped>
.underline-on-hover:hover {
  @apply cursor-pointer underline;
}

.warning {
  @apply text-error;
}
</style>
